/* eslint-disable max-lines, max-lines-per-function */
import { useCallback, useMemo, useRef } from "react";
import { useArtwork, useArtworkForm } from "@design-lab/entities/artwork";
import {
  calculatedArtworkDimensions,
  type Surface
} from "@design-lab/entities/artwork/lib/calculateDimensions";
import type { Artwork } from "@design-lab/shared/types/artwork";
import { useCompositionForm } from "@design-lab/entities/composition";
import { apiResponseToFormData } from "@design-lab/entities/composition/lib/design-project";
import type {
  DesignLabFormType,
  DesignProjectResponse,
  MadeEditResponse,
  PlacementFormPosition
} from "@design-lab/shared/types";
import type { models } from "jiffy-api";
import type { printOnDemand } from "common/utils/models";
import { notifyError } from "common/utils/app-monitoring/notify";
import {
  artworkPlacementPath,
  artworkSizePath,
  infoPath,
  artworkPlacementFieldPath,
  ARTWORK_PLACEMENT_FIELDS
} from "@design-lab/shared/lib/formPathHelpers";

type Size = { width: number; height: number };

// Source size's width/height ratio equals the natural image aspect ratio by
// construction (see calculateDimensions.ts:48-62), so we can feed a synthetic
// HTMLImageElement with the source dims to derive target-surface bounds
// without needing the real cached image.
function clampSizeToTargetSurface(sourceSize: Size, targetSurface: Surface | undefined): Size {
  if (!targetSurface) return sourceSize;

  const syntheticImage = {
    naturalWidth: sourceSize.width,
    naturalHeight: sourceSize.height
  } as HTMLImageElement;

  const { maxWidthInches, maxHeightInches } = calculatedArtworkDimensions(
    syntheticImage,
    targetSurface
  );

  if (!maxWidthInches || !maxHeightInches) return sourceSize;

  if (sourceSize.width <= maxWidthInches && sourceSize.height <= maxHeightInches) {
    return sourceSize;
  }

  return { width: maxWidthInches, height: maxHeightInches };
}

const ARTWORK_FIELDS: Array<{ key: PlacementFormPosition }> = [
  { key: "front" },
  { key: "back" },
  { key: "frontLeftChest" }
] as const;

export type UseFormRestorationParams = {
  projectData: models.designLab.getLatestDesignProject.Response | null;
  currentProduct: printOnDemand.getProduct.Response | undefined;
  compositionId?: string | null;
  productsMap: Map<string, printOnDemand.getProduct.Composition & { catalogId: string }>;
  shouldClearTargetSide?: boolean;
  targetSide?: "front" | "back";
  preserveExistingArtwork?: boolean;
};

export type UseFormRestorationReturn = {
  restoreFromProject: () => Promise<void>;
  restoreArtworkOnly: () => void;
  clearTargetSideArtwork: (side: "front" | "back") => void;
  syncFrontPlacements: () => void;
  getRestoredData: () => Partial<DesignLabFormType> | null;
  lastSavedDataRef: React.MutableRefObject<string | null>;
};

export function useFormRestoration(params: UseFormRestorationParams): UseFormRestorationReturn {
  const {
    projectData,
    currentProduct,
    compositionId,
    productsMap,
    shouldClearTargetSide = false,
    targetSide = "front",
    preserveExistingArtwork = false
  } = params;

  const { form: artworkForm } = useArtworkForm();
  const { form: compositionForm, registerValidationForAllCompositions } = useCompositionForm();
  const { front, back } = useArtwork();
  const lastSavedDataRef = useRef<string | null>(null);

  const surfaces = useMemo(
    () => currentProduct?.compositions?.find(c => c.id === compositionId)?.surfaces,
    [currentProduct, compositionId]
  );

  const syncFrontPlacements = useCallback(() => {
    const artwork = artworkForm.getValues();
    const hasFront = artwork.front?.artworkId;
    const hasFrontLeftChest = artwork.frontLeftChest?.artworkId;

    if (hasFront && !hasFrontLeftChest) {
      // Only copy if front has valid dimensions to prevent artworkId with 0x0
      const hasValidSize = artwork.front.size?.width && artwork.front.size?.height;
      if (hasValidSize) {
        const clampedSize = clampSizeToTargetSurface(artwork.front.size, surfaces?.frontLeftChest);
        artworkForm.setValue(artworkPlacementPath("frontLeftChest"), {
          ...artwork.front,
          size: clampedSize
        });
        artworkForm.setValue(artworkSizePath("frontLeftChest"), clampedSize, {
          shouldDirty: true,
          shouldTouch: true
        });
      }
    } else if (!hasFront && hasFrontLeftChest) {
      // Only copy if frontLeftChest has valid dimensions to prevent artworkId with 0x0
      const hasValidSize =
        artwork.frontLeftChest.size?.width && artwork.frontLeftChest.size?.height;
      if (hasValidSize) {
        const clampedSize = clampSizeToTargetSurface(artwork.frontLeftChest.size, surfaces?.front);
        artworkForm.setValue(artworkPlacementPath("front"), {
          ...artwork.frontLeftChest,
          size: clampedSize
        });
        artworkForm.setValue(artworkSizePath("front"), clampedSize, {
          shouldDirty: true,
          shouldTouch: true
        });
      }
    }
  }, [artworkForm, surfaces]);

  const clearTargetSideArtwork = useCallback(
    (side: "front" | "back") => {
      if (side === "back") {
        artworkForm.setValue("back", { artworkId: "", size: { width: 0, height: 0 } });
        back.send({ type: "reset" });
      } else {
        // Clear both front placements
        artworkForm.setValue("front", { artworkId: "", size: { width: 0, height: 0 } });
        artworkForm.setValue("frontLeftChest", { artworkId: "", size: { width: 0, height: 0 } });
        front.send({ type: "reset" });
      }
    },
    [artworkForm, front, back]
  );

  const restoreArtworkOnly = useCallback(() => {
    if (!projectData?.placements) {
      return;
    }

    const { formData: restoredData } = apiResponseToFormData(
      projectData as unknown as DesignProjectResponse | MadeEditResponse,
      compositionId || "",
      currentProduct || undefined
    );

    if (!restoredData.artwork) return;

    // Restore artwork fields
    ARTWORK_FIELDS.forEach(({ key }) => {
      const artworkItem = restoredData.artwork![key];
      if (!artworkItem?.artworkId) return;

      const hasValidSize = artworkItem.size?.width && artworkItem.size?.height;

      // Only restore if size is valid to prevent artworkId with 0x0 dimensions
      // This prevents InvalidDtfVariantPayload errors when adding to cart
      if (!hasValidSize) {
        // Log to app monitoring when invalid dimensions are detected during restoration
        notifyError("Design Lab: Skipping artwork restoration due to invalid dimensions", {
          params: {
            placement: key,
            artworkId: artworkItem.artworkId,
            size: artworkItem.size,
            hasSize: !!artworkItem.size,
            width: artworkItem.size?.width,
            height: artworkItem.size?.height
          }
        });
        return;
      }

      // Set artworkId and size together only when both are valid
      artworkForm.setValue(artworkPlacementPath(key), artworkItem);
      artworkForm.setValue(artworkSizePath(key), artworkItem.size, {
        shouldDirty: true,
        shouldTouch: true
      });
    });

    // Sync front placements
    syncFrontPlacements();

    // Restore placement fields
    ARTWORK_PLACEMENT_FIELDS.forEach(field => {
      const fieldPath = artworkPlacementFieldPath(field);
      if (fieldPath && restoredData.artwork![field as keyof typeof restoredData.artwork]) {
        artworkForm.setValue(
          fieldPath,
          restoredData.artwork![field as keyof typeof restoredData.artwork]
        );
      }
    });

    // Restore info fields
    if (restoredData.artwork!.info?.front) {
      artworkForm.setValue(
        infoPath("front", "editImageManual"),
        Boolean(restoredData.artwork!.info.front.editImageManual)
      );
    }
    if (restoredData.artwork!.info?.back) {
      artworkForm.setValue(
        infoPath("back", "editImageManual"),
        Boolean(restoredData.artwork!.info.back.editImageManual)
      );
    }
  }, [projectData, compositionId, currentProduct, artworkForm, syncFrontPlacements]);

  const enhanceCompositionsWithProductData = useCallback(
    (compositions: DesignLabFormType["compositions"]) => {
      if (!compositions) return compositions;

      return {
        ...compositions,
        addedCompositions: compositions.addedCompositions.map(comp => {
          const productData = productsMap.get(comp.id);
          if (!productData) return comp;

          return {
            ...comp,
            colorName: productData.colorName || comp.colorName
          };
        })
      };
    },
    [productsMap]
  );

  const restoreFromProject = useCallback(async () => {
    if (!projectData) {
      return;
    }

    // For existing projects without URL params, we might not have currentProduct yet
    // In this case, apiResponseToFormData should handle it gracefully
    const { formData: restoredData } = apiResponseToFormData(
      projectData as unknown as DesignProjectResponse | MadeEditResponse,
      compositionId || "",
      currentProduct || undefined // Allow undefined, apiResponseToFormData should handle it
    );

    // Handle artwork restoration based on flags
    if (shouldClearTargetSide) {
      // Clear target side before restoration (for modals)
      clearTargetSideArtwork(targetSide);

      // Restore non-target side artwork only
      if (restoredData.artwork) {
        const mergedArtwork = { ...restoredData.artwork };

        if (targetSide === "back") {
          // Keep front artwork, clear back
          mergedArtwork.back = { artworkId: "", size: { width: 0, height: 0 } };
        } else {
          // Keep back artwork, clear front placements
          mergedArtwork.front = { artworkId: "", size: { width: 0, height: 0 } };
          mergedArtwork.frontLeftChest = { artworkId: "", size: { width: 0, height: 0 } };
        }

        artworkForm.reset(mergedArtwork);
      }
    } else if (preserveExistingArtwork) {
      // Merge with existing artwork (for auto-upload)
      const currentArtwork = artworkForm.getValues();
      if (restoredData.artwork) {
        const mergedArtwork = {
          ...restoredData.artwork,
          ...currentArtwork,
          info: {
            ...restoredData.artwork.info,
            ...currentArtwork.info
          }
        };

        // Preserve existing placement data that's not being auto-uploaded
        if (restoredData.artwork.back && !currentArtwork.back?.artworkId) {
          mergedArtwork.back = restoredData.artwork.back;
        }
        if (restoredData.artwork.front && !currentArtwork.front?.artworkId) {
          mergedArtwork.front = restoredData.artwork.front;
        }
        if (restoredData.artwork.frontLeftChest && !currentArtwork.frontLeftChest?.artworkId) {
          mergedArtwork.frontLeftChest = restoredData.artwork.frontLeftChest;
        }

        artworkForm.reset(mergedArtwork);
      }
    } else {
      // Full restoration (no URL params case)
      restoreArtworkOnly();
    }

    // Reset artwork state machines
    if (projectData?.placements?.back?.dtfArtwork) {
      back.send({
        type: "reset",
        targetState: "finished",
        context: {
          visitedStates: [],
          artworkId: projectData.placements.back.dtfArtwork.id,
          artwork: projectData.placements.back.dtfArtwork as unknown as Artwork,
          cacheWarmingStatus: "none",
          shouldRedirect: true
        }
      });
    }

    const frontPlacementData =
      projectData?.placements?.front || projectData?.placements?.frontLeftChest;
    if (frontPlacementData?.dtfArtwork) {
      front.send({
        type: "reset",
        targetState: "finished",
        context: {
          visitedStates: [],
          artworkId: frontPlacementData.dtfArtwork.id,
          artwork: frontPlacementData.dtfArtwork as unknown as Artwork,
          cacheWarmingStatus: "none",
          shouldRedirect: true
        }
      });
    }

    // Restore compositions
    if (restoredData.compositions) {
      const enhancedCompositions = enhanceCompositionsWithProductData(restoredData.compositions);

      // Reset the form with the restored compositions to ensure clean state
      compositionForm.reset({
        addedCompositions: enhancedCompositions.addedCompositions,
        currentCompositionId: enhancedCompositions.currentCompositionId
      });

      // Also set values to ensure they're updated
      compositionForm.setValue("addedCompositions", enhancedCompositions.addedCompositions, {
        shouldValidate: true,
        shouldDirty: false
      });
      compositionForm.setValue("currentCompositionId", enhancedCompositions.currentCompositionId, {
        shouldValidate: true,
        shouldDirty: false
      });

      registerValidationForAllCompositions();

      // Store initial data for comparison
      if (restoredData.compositions.currentCompositionId) {
        const initialPayload = JSON.stringify({
          artwork: restoredData.artwork,
          compositions: restoredData.compositions
        });
        lastSavedDataRef.current = initialPayload;
      }
    }
  }, [
    projectData,
    compositionId,
    currentProduct,
    shouldClearTargetSide,
    targetSide,
    preserveExistingArtwork,
    artworkForm,
    compositionForm,
    front,
    back,
    restoreArtworkOnly,
    clearTargetSideArtwork,
    enhanceCompositionsWithProductData,
    registerValidationForAllCompositions
  ]);

  const getRestoredData = useCallback(() => {
    if (!projectData) return null;

    const { formData } = apiResponseToFormData(
      projectData as unknown as DesignProjectResponse | MadeEditResponse,
      compositionId || "",
      currentProduct
    );

    return formData;
  }, [projectData, compositionId, currentProduct]);

  return {
    restoreFromProject,
    restoreArtworkOnly,
    clearTargetSideArtwork,
    syncFrontPlacements,
    getRestoredData,
    lastSavedDataRef
  };
}
